import axios from 'axios';
import { getAuthToken, getCurrentUserId } from '../utils/authUtils';
import socketService from './socketService';
import { getApiBaseUrl } from '../utils/apiConfig';
import offlineQueue from '../utils/offlineQueue';

// Use getApiBaseUrl to get the correct API URL with /api prefix
const API_URL = getApiBaseUrl();

// Create axios instance with auth header
const authAxios = axios.create({
  baseURL: API_URL + '/messages',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add authorization header to every request
authAxios.interceptors.request.use(
  (config) => {
    // Get a fresh token for each request to ensure we have the latest
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`Adding auth token to request: ${token.substring(0, 10)}...`);
    } else {
      console.warn('No auth token available for request');

      // Instead of throwing an error, redirect to login page if in a browser environment
      if (typeof window !== 'undefined') {
        console.log('Redirecting to login page due to missing authentication');
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }

      // Still throw error to prevent further execution
      throw new Error('Authentication required');
    }

    // Log the full URL being requested for debugging
    console.log(`Requesting: ${config.baseURL}${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
authAxios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 errors and some 403 errors, but NOT chat locked errors
    if (error.response && error.response.status === 401) {
      console.error('Authentication error in messageService: 401 Unauthorized');

      // Clear any invalid auth data
      localStorage.removeItem('token');

      // Redirect to login page if in a browser environment
      if (typeof window !== 'undefined') {
        console.log('Redirecting to login page due to authentication failure');
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }

      return Promise.reject(new Error('Authentication required'));
    } else if (error.response && error.response.status === 403) {
      // Check if this is a CHAT_LOCKED error vs authentication error
      const errorCode = error.response.data?.code;
      const errorMessage = error.response.data?.message;

      if (errorCode === 'CHAT_LOCKED' || errorMessage?.includes('Chat is locked')) {
        console.log('Chat is locked - not an authentication error');
        // This is a business logic error, not an auth error - pass it through
        return Promise.reject(error);
      } else {
        // This is an actual authentication/authorization error
        console.error('Authorization error in messageService: 403 Forbidden');

        // Clear any invalid auth data
        localStorage.removeItem('token');

        // Redirect to login page if in a browser environment
        if (typeof window !== 'undefined') {
          console.log('Redirecting to login page due to authorization failure');
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }

        return Promise.reject(new Error('Authorization required'));
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Get threads for the current user
 */
const getThreads = async () => {
  try {
    // Check if we have a valid token first
    const token = getAuthToken();
    if (!token) {
      console.error('Cannot fetch threads: No valid authentication token');
      return [];
    }

    // Try the real API with valid token
    const response = await authAxios.get('/threads');

    if (!response?.data?.data) {
      console.warn('API returned invalid data format for threads');
      throw new Error('Invalid API response format');
    }

    console.log('Successfully retrieved threads from API');

    // Ensure we always return an array
    const threads = response.data.data.threads;
    if (!Array.isArray(threads)) {
      console.warn('API returned non-array for threads:', threads);
      return [];
    }

    return threads;
  } catch (error) {
    console.error('Error fetching threads:', error);
    return [];
  }
};

/**
 * Get messages for a specific thread
 * @param {string} threadId - ID of the thread
 * @returns {Promise<Array>} - Array of messages
 */
const getMessages = async (threadId) => {
  if (!threadId) {
    console.error('Invalid thread ID provided to getMessages');
    return [];
  }

  try {
    console.log(`[messageService] Getting messages for thread ${threadId}`);
    const response = await authAxios.get(`/threads/${threadId}`);

    // Check if response.data has the messages array
    if (response?.data?.data?.messages) {
      console.log(`[messageService] Successfully fetched ${response.data.data.messages.length} messages`);
      return response.data.data.messages;
    } else if (response?.data?.messages) {
      console.log(`[messageService] Successfully fetched ${response.data.messages.length} messages`);
      return response.data.messages;
    } else if (response?.data && Array.isArray(response.data)) {
      console.log(`[messageService] Successfully fetched ${response.data.length} messages`);
      return response.data;
    } else {
      console.warn('[messageService] API returned invalid data format for messages');
      return [];
    }
  } catch (error) {
    console.error(`[messageService] Error fetching messages for thread ${threadId}:`, error);

    // Check if it's a network error
    if (error.isAxiosError && !error.response) {
      console.error('[messageService] Network error detected');
    }

    return [];
  }
};

/**
 * Send a message in a thread
 * @param {string} threadId - ID of the thread
 * @param {string} content - Text content of the message
 * @param {Array} attachments - Optional file attachments
 * @param {string} replyToId - Optional ID of the message being replied to
 * @returns {Promise<Object>} - The sent message
 */
const sendMessage = async (threadId, content, attachments = [], replyToId = null) => {
  if (!threadId) {
    console.error('Invalid thread ID');
    return null;
  }

  try {
    const messageData = { content };

    // Add replyToId if replying to a message
    if (replyToId) {
      messageData.replyToId = replyToId;
      messageData.messageType = 'reply';
    }

    // Add file data if attachments present
    if (attachments && attachments.length > 0) {
      const firstAttachment = attachments[0];
      messageData.file = {
        fileName: firstAttachment.name,
        fileType: firstAttachment.type,
        fileSize: firstAttachment.size,
        filePath: firstAttachment.url || ''
      };
      messageData.messageType = 'file';
    }

    // Send the message to the backend
    const response = await authAxios.post(`/threads/${threadId}`, messageData);

    if (response.data && response.data.status === 'success') {
      const savedMessage = response.data.data.message;
      console.log('[messageService] Message sent successfully via HTTP API');
      return savedMessage;
    }

    return null;
  } catch (error) {
    console.error('[messageService] Error sending message via HTTP:', error);

    // Check if it's a network error
    if (error.isAxiosError && !error.response) {
      console.log('[messageService] Network error detected, queueing message for retry');

      // Queue the request for retry when connection is restored
      const queueId = offlineQueue.enqueue({
        method: 'POST',
        url: `/threads/${threadId}`,
        data: {
          content,
          replyToId,
          messageType: replyToId ? 'reply' : (attachments.length > 0 ? 'file' : 'text'),
          file: attachments.length > 0 ? {
            fileName: attachments[0].name,
            fileType: attachments[0].type,
            fileSize: attachments[0].size,
            filePath: attachments[0].url || ''
          } : null
        },
        headers: {
          Authorization: `Bearer ${getAuthToken()}`
        },
        metadata: {
          action: 'sendMessage',
          threadId,
          preview: content.substring(0, 50)
        }
      });

      // Return a temporary message for optimistic UI
      return {
        _id: `temp-${Date.now()}`,
        threadId,
        content,
        sender: { _id: getCurrentUserId() },
        createdAt: new Date().toISOString(),
        replyTo: replyToId,
        messageType: replyToId ? 'reply' : (attachments.length > 0 ? 'file' : 'text'),
        file: attachments.length > 0 ? {
          fileName: attachments[0].name,
          fileType: attachments[0].type,
          fileSize: attachments[0].size,
          filePath: attachments[0].url || ''
        } : null,
        isLocal: true,
        queueId
      };
    }

    // For other errors, try Socket.IO as fallback
    try {
      console.log('[messageService] Attempting Socket.IO fallback');
      socketService.sendMessage(threadId, {
        content,
        replyToId,
        messageType: replyToId ? 'reply' : (attachments.length > 0 ? 'file' : 'text'),
        file: attachments.length > 0 ? {
          fileName: attachments[0].name,
          fileType: attachments[0].type,
          fileSize: attachments[0].size,
          filePath: attachments[0].url || ''
        } : null
      });

      return {
        _id: `temp-${Date.now()}`,
        threadId,
        content,
        sender: { _id: getCurrentUserId() },
        createdAt: new Date().toISOString(),
        replyTo: replyToId,
        messageType: replyToId ? 'reply' : (attachments.length > 0 ? 'file' : 'text'),
        file: attachments.length > 0 ? {
          fileName: attachments[0].name,
          fileType: attachments[0].type,
          fileSize: attachments[0].size,
          filePath: attachments[0].url || ''
        } : null,
        isLocal: true
      };
    } catch (socketError) {
      console.error('[messageService] Socket fallback also failed:', socketError);
      return null;
    }
  }
};

/**
 * Create a new thread
 * @param {string} participantId - ID of the other participant
 * @param {string} initialMessage - Optional initial message
 * @param {Array} attachments - Optional file attachments
 * @returns {Promise<Object>} - The created thread
 */
const createThread = async (participantId, initialMessage, attachments = []) => {
  try {
    const response = await authAxios.post('/threads', {
      participantId,
      initialMessage,
      attachments
    });

    console.log('[messageService] Thread created successfully via API');
    return response.data.data.thread;
  } catch (error) {
    console.error('[messageService] Error creating thread via API:', error);

    // Check if it's a network error
    if (error.isAxiosError && !error.response) {
      console.log('[messageService] Network error detected, queueing thread creation');

      // Queue the request for retry
      offlineQueue.enqueue({
        method: 'POST',
        url: '/threads',
        data: {
          participantId,
          initialMessage,
          attachments
        },
        headers: {
          Authorization: `Bearer ${getAuthToken()}`
        },
        metadata: {
          action: 'createThread',
          participantId,
          preview: initialMessage ? initialMessage.substring(0, 50) : 'New conversation'
        }
      });
    }

    throw error;
  }
};

/**
 * Mark a thread as read
 * @param {string} threadId - ID of the thread
 * @returns {Promise<Object>} - Response data
 */
const markThreadAsRead = async (threadId) => {
  try {
    const response = await authAxios.put(`/threads/${threadId}/read`);
    console.log(`[messageService] Thread ${threadId} marked as read`);
    return response.data.data;
  } catch (error) {
    console.error('[messageService] Error marking thread as read:', error);

    // Check if it's a network error
    if (error.isAxiosError && !error.response) {
      console.log('[messageService] Network error detected, queueing mark as read');

      offlineQueue.enqueue({
        method: 'PUT',
        url: `/threads/${threadId}/read`,
        data: {},
        headers: {
          Authorization: `Bearer ${getAuthToken()}`
        },
        metadata: {
          action: 'markAsRead',
          threadId
        }
      });
    }

    throw error;
  }
};

/**
 * Archive a thread
 * @param {string} threadId - ID of the thread
 * @returns {Promise<Object>} - Response data
 */
const archiveThread = async (threadId) => {
  try {
    const response = await authAxios.put(`/threads/${threadId}/archive`);
    console.log(`[messageService] Thread ${threadId} archived`);
    return response.data.data;
  } catch (error) {
    console.error('[messageService] Error archiving thread:', error);

    // Check if it's a network error
    if (error.isAxiosError && !error.response) {
      console.log('[messageService] Network error detected, queueing archive');

      offlineQueue.enqueue({
        method: 'PUT',
        url: `/threads/${threadId}/archive`,
        data: {},
        headers: {
          Authorization: `Bearer ${getAuthToken()}`
        },
        metadata: {
          action: 'archiveThread',
          threadId
        }
      });
    }

    throw error;
  }
};

/**
 * Process offline queue
 * Called when connection is restored
 */
const processOfflineQueue = async () => {
  console.log('[messageService] Processing offline queue...');

  const executor = async (queueItem) => {
    try {
      // Create axios config from queue item
      const config = {
        method: queueItem.method.toLowerCase(),
        url: queueItem.url,
        data: queueItem.data,
        headers: {
          ...queueItem.headers,
          // Refresh token in case it changed
          Authorization: `Bearer ${getAuthToken()}`
        }
      };

      await authAxios.request(config);
      console.log(`[messageService] Successfully processed queued ${queueItem.metadata?.action}`);
    } catch (error) {
      console.error(`[messageService] Failed to process queued ${queueItem.metadata?.action}:`, error);
      throw error;
    }
  };

  const result = await offlineQueue.processQueue(executor);
  console.log('[messageService] Offline queue processing complete:', result);

  return result;
};

// Export message service methods
const messageService = {
  getThreads,
  getMessages,
  sendMessage,
  createThread,
  markThreadAsRead,
  archiveThread,
  processOfflineQueue
};

export default messageService;
